import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, X, Download, Trash2, Edit3, Paperclip, TrendingUp, TrendingDown, ReceiptText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
// UK tax year starts 6 April — the HMRC view Justina's accountant cares about.
const taxYearStart = () => {
  const d = new Date();
  const y = d.getMonth() + 1 >= 4 && (d.getMonth() + 1 > 4 || d.getDate() >= 6) ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-04-06`;
};

const PRESETS = [
  { id: "this-month", label: "This month", range: () => [monthStart(), todayStr()] },
  { id: "last-month", label: "Last month", range: () => {
      const d = new Date(); d.setDate(1); d.setDate(0);
      const end = d.toISOString().slice(0, 10);
      return [`${end.slice(0, 7)}-01`, end];
    } },
  { id: "tax-year", label: "Tax year to date", range: () => [taxYearStart(), todayStr()] },
  { id: "last-12", label: "Last 12 months", range: () => {
      const d = new Date(); d.setFullYear(d.getFullYear() - 1);
      return [d.toISOString().slice(0, 10), todayStr()];
    } },
];

const monthLabel = (mk) => new Date(`${mk}-02T00:00:00`).toLocaleString("en-GB", { month: "short", year: "numeric" });

const EMPTY_FORM = { date: todayStr(), category: "", supplier: "", amount: "", vat_amount: "", note: "", recurring: "none" };

const AdminAccounts = () => {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed; {mode:'add'|'edit', id?, fields}
  const [busy, setBusy] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, x, c] = await Promise.all([
        axios.get(`${API}/admin/accounts/summary`, { params: { start_date: start, end_date: end } }),
        axios.get(`${API}/admin/accounts/expenses`, { params: { start_date: start, end_date: end } }),
        axios.get(`${API}/admin/accounts/categories`),
      ]);
      setSummary(s.data);
      setExpenses(x.data.items || []);
      setCategories(c.data.categories || []);
    } catch {
      toast.error("Couldn't load the accounts.");
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => {
    const [s, e] = p.range();
    setStart(s); setEnd(e);
  };

  const openAdd = () => { setForm({ mode: "add", fields: { ...EMPTY_FORM } }); setReceiptFile(null); };
  const openEdit = (x) => {
    setForm({
      mode: "edit", id: x.id,
      fields: {
        date: x.date, category: x.category, supplier: x.supplier || "",
        amount: String(x.amount), vat_amount: x.vat_amount ? String(x.vat_amount) : "",
        note: x.note || "", recurring: x.recurring || "none",
      },
    });
    setReceiptFile(null);
  };

  const saveExpense = async () => {
    const f = form.fields;
    if (!f.category.trim()) { toast.error("Pick or type a category."); return; }
    const amount = parseFloat(f.amount);
    if (!amount || amount <= 0) { toast.error("Enter an amount."); return; }
    const body = {
      date: f.date,
      category: f.category.trim(),
      supplier: f.supplier.trim(),
      amount,
      vat_amount: parseFloat(f.vat_amount) || 0,
      note: f.note.trim(),
      recurring: f.recurring,
    };
    setBusy(true);
    try {
      let id = form.id;
      if (form.mode === "add") {
        const { data } = await axios.post(`${API}/admin/accounts/expenses`, body);
        id = data.expense.id;
      } else {
        await axios.put(`${API}/admin/accounts/expenses/${form.id}`, body);
      }
      if (receiptFile && id) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        await axios.post(`${API}/admin/accounts/expenses/${id}/receipt`, fd);
      }
      toast.success(form.mode === "add"
        ? (body.recurring === "monthly" ? "Expense added — it will repeat every month automatically." : "Expense added to the vault.")
        : "Expense updated.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the expense.");
    } finally {
      setBusy(false);
    }
  };

  const deleteExpense = async (x) => {
    if (!window.confirm(`Delete this expense?\n\n${x.date} · ${x.category} · ${gbp(x.amount)}\n\nThis can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/accounts/expenses/${x.id}`);
      toast.success("Expense deleted.");
      load();
    } catch {
      toast.error("Couldn't delete the expense.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/accounts/export.csv`, {
        params: { start_date: start, end_date: end },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `walnut-lakes-accounts-${start}-to-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded — ready for the accountant or HMRC.");
    } catch {
      toast.error("Couldn't build the CSV.");
    }
  };

  return (
    <section data-testid="accounts-section">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#6A5F58] mb-2">Money in · money out</p>
          <h2 className="font-heading text-3xl md:text-4xl font-light text-[#14100F]">Accounts & HMRC vault</h2>
          <p className="text-[#6A5F58] mt-1 text-sm max-w-xl">
            Revenue flows in automatically from bookings. Add the park's bills by hand — every record is kept
            permanently with its receipt, ready to export for the accountant or HMRC.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className="px-3 h-10 rounded-lg border border-[#DDD1BE] text-[#14100F] text-[10px] uppercase tracking-[0.12em] hover:bg-[#F5EFE6] transition"
              data-testid={`accounts-preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 rounded-lg w-40" data-testid="accounts-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 rounded-lg w-40" data-testid="accounts-end" />
          </div>
          <button
            onClick={exportCsv}
            className="h-10 px-4 rounded-lg bg-[#0F0E0E] text-[#F5EFE6] text-[11px] uppercase tracking-[0.14em] inline-flex items-center gap-2 hover:bg-[#8C4E58] transition-colors"
            data-testid="accounts-export-csv"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {loading && !summary ? (
        <div className="py-16 text-center text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Balancing the books…</div>
      ) : summary && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="accounts-kpi-revenue">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Revenue (paid)</p>
              <p className="font-heading text-3xl text-[#14100F] mt-1">{gbp(summary.revenue_total)}</p>
              <p className="text-[11px] text-[#6A5F58] mt-1">Lodges {gbp(summary.revenue_by_type.lodge)} · Pitches {gbp(summary.revenue_by_type.touring)}</p>
            </div>
            <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="accounts-kpi-expenses">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Expenses</p>
              <p className="font-heading text-3xl text-[#14100F] mt-1">{gbp(summary.expense_total)}</p>
              <p className="text-[11px] text-[#6A5F58] mt-1">{summary.expense_count} bills · VAT {gbp(summary.vat_on_expenses)}</p>
            </div>
            <div className={`rounded-2xl p-5 border-2 ${summary.net >= 0 ? "bg-[#3B8C4E]/[0.07] border-[#3B8C4E]/40" : "bg-[#8C2E39]/[0.06] border-[#8C2E39]/40"}`} data-testid="accounts-kpi-net">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Net profit</p>
              <p className={`font-heading text-3xl mt-1 flex items-center gap-2 ${summary.net >= 0 ? "text-[#2A6636]" : "text-[#8C2E39]"}`}>
                {summary.net >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />} {gbp(summary.net)}
              </p>
              <p className="text-[11px] text-[#6A5F58] mt-1">{summary.range.start_date} → {summary.range.end_date}</p>
            </div>
            <div className="bg-white border border-[#DDD1BE] rounded-2xl p-5 flex flex-col justify-between">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Add a bill</p>
              <button
                onClick={openAdd}
                className="mt-2 h-11 rounded-xl bg-[#8C4E58] text-white text-[11px] uppercase tracking-[0.14em] font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#6d3a43] transition"
                data-testid="accounts-add-expense"
              >
                <Plus size={15} /> Add expense
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Monthly P&L */}
            <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6" data-testid="accounts-monthly">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6A5F58] mb-4">Profit & loss by month</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#6A5F58]">
                    <th className="pb-2">Month</th><th className="pb-2 text-right">Revenue</th><th className="pb-2 text-right">Expenses</th><th className="pb-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.months.map((m) => (
                    <tr key={m.month} className="border-t border-[#EDE3D2]" data-testid={`accounts-month-${m.month}`}>
                      <td className="py-2 text-[#14100F] font-medium">{monthLabel(m.month)}</td>
                      <td className="py-2 text-right text-[#2A6636]">{gbp(m.revenue)}</td>
                      <td className="py-2 text-right text-[#8C2E39]">{gbp(m.expenses)}</td>
                      <td className={`py-2 text-right font-semibold ${m.net >= 0 ? "text-[#2A6636]" : "text-[#8C2E39]"}`}>{gbp(m.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expenses by category */}
            <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6" data-testid="accounts-by-category">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6A5F58] mb-4">Expenses by category</p>
              {summary.expense_by_category.length === 0 ? (
                <p className="text-sm text-[#6A5F58]">No expenses in this range yet — add the first bill above.</p>
              ) : (
                <div className="space-y-2">
                  {summary.expense_by_category.map((c) => (
                    <div key={c.category} className="flex items-center gap-3" data-testid={`accounts-cat-${c.category}`}>
                      <span className="text-sm text-[#14100F] w-44 truncate">{c.category}</span>
                      <div className="flex-1 h-2 rounded-full bg-[#EDE3D2] overflow-hidden">
                        <div className="h-full bg-[#8C4E58]" style={{ width: `${summary.expense_total ? Math.min(100, (c.amount / summary.expense_total) * 100) : 0}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-[#14100F] w-24 text-right">{gbp(c.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Expense ledger */}
          <div className="bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden" data-testid="accounts-ledger">
            <div className="p-5 border-b border-[#DDD1BE] flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.2em] text-[#6A5F58] font-semibold">Expense ledger · {expenses.length}</p>
              <p className="text-[11px] text-[#6A5F58]">Monthly bills marked ↻ repeat automatically</p>
            </div>
            {expenses.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#6A5F58]">Nothing here yet — salaries, water, electric, council tax… add them as they land.</div>
            ) : (
              expenses.map((x) => (
                <div key={x.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b border-[#EDE3D2] last:border-0" data-testid={`expense-row-${x.id}`}>
                  <div className="min-w-[220px]">
                    <p className="text-sm font-medium text-[#14100F]">
                      {x.category}
                      {x.recurring === "monthly" && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#C89B5F]/15 text-[#8a6a3a] text-[9px] uppercase tracking-[0.1em]" title="Repeats monthly">↻ monthly</span>}
                    </p>
                    <p className="text-[11px] text-[#6A5F58] mt-0.5">
                      {x.date}{x.supplier ? ` · ${x.supplier}` : ""}{x.note ? ` · ${x.note}` : ""}{x.vat_amount ? ` · VAT ${gbp(x.vat_amount)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {x.receipt_url && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${x.receipt_url}`} target="_blank" rel="noreferrer"
                         className="p-2 rounded-full text-[#6A5F58] hover:text-[#8C4E58] hover:bg-[#8C4E58]/10 transition"
                         title="View receipt (vault copy)" data-testid={`expense-receipt-${x.id}`}>
                        <Paperclip size={14} />
                      </a>
                    )}
                    <span className="font-mono font-semibold text-[#14100F]">{gbp(x.amount)}</span>
                    <button onClick={() => openEdit(x)} className="p-2 rounded-full text-[#6A5F58] hover:text-[#0F0E0E] hover:bg-[#F5EFE6] transition" title="Edit" data-testid={`expense-edit-${x.id}`}>
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => deleteExpense(x)} className="p-2 rounded-full text-[#6A5F58] hover:text-[#8C2E39] hover:bg-[#8C2E39]/10 transition" title="Delete" data-testid={`expense-delete-${x.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Add / edit expense modal */}
      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setForm(null)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="expense-modal">
            <div className="p-5 bg-[#F5EFE6] border-b border-[#DDD1BE] flex items-center justify-between">
              <h3 className="font-heading text-xl text-[#14100F]">{form.mode === "add" ? "Add expense" : "Edit expense"}</h3>
              <button onClick={() => setForm(null)} className="p-1.5 rounded-full hover:bg-[#DDD1BE]" data-testid="expense-modal-close"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Date</Label>
                  <Input type="date" value={form.fields.date} onChange={(e) => setForm({ ...form, fields: { ...form.fields, date: e.target.value } })} className="mt-1 h-10 rounded-lg" data-testid="expense-date" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Amount £</Label>
                  <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.fields.amount} onChange={(e) => setForm({ ...form, fields: { ...form.fields, amount: e.target.value } })} className="mt-1 h-10 rounded-lg" data-testid="expense-amount" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Category</Label>
                <select
                  value={categories.includes(form.fields.category) ? form.fields.category : (form.fields.category ? "__custom" : "")}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== "__custom") setForm({ ...form, fields: { ...form.fields, category: v } });
                  }}
                  className="mt-1 w-full h-10 rounded-lg border border-[#DDD1BE] bg-white px-3 text-sm"
                  data-testid="expense-category"
                >
                  <option value="">Pick a category…</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value="__custom">Type my own ↓</option>
                </select>
                {(!categories.includes(form.fields.category) || form.fields.category === "") && (
                  <Input
                    placeholder="Or type a new category…"
                    value={form.fields.category}
                    onChange={(e) => setForm({ ...form, fields: { ...form.fields, category: e.target.value } })}
                    className="mt-2 h-10 rounded-lg"
                    data-testid="expense-category-custom"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Supplier</Label>
                  <Input placeholder="e.g. Anglian Water" value={form.fields.supplier} onChange={(e) => setForm({ ...form, fields: { ...form.fields, supplier: e.target.value } })} className="mt-1 h-10 rounded-lg" data-testid="expense-supplier" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">VAT included £</Label>
                  <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.fields.vat_amount} onChange={(e) => setForm({ ...form, fields: { ...form.fields, vat_amount: e.target.value } })} className="mt-1 h-10 rounded-lg" data-testid="expense-vat" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Note</Label>
                <Input placeholder="Optional note" value={form.fields.note} onChange={(e) => setForm({ ...form, fields: { ...form.fields, note: e.target.value } })} className="mt-1 h-10 rounded-lg" data-testid="expense-note" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#14100F] cursor-pointer" data-testid="expense-recurring-wrap">
                <input
                  type="checkbox"
                  checked={form.fields.recurring === "monthly"}
                  onChange={(e) => setForm({ ...form, fields: { ...form.fields, recurring: e.target.checked ? "monthly" : "none" } })}
                  className="w-4 h-4 accent-[#8C4E58]"
                  data-testid="expense-recurring"
                />
                Repeats every month (salaries, council tax…)
              </label>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Receipt (photo or PDF — kept in the vault)</Label>
                <input type="file" accept="image/*,application/pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} className="mt-1 text-sm" data-testid="expense-receipt-file" />
              </div>
            </div>
            <div className="p-5 border-t border-[#DDD1BE] flex gap-2">
              <button onClick={() => setForm(null)} disabled={busy} className="flex-1 h-11 rounded-xl border border-[#DDD1BE] text-[#14100F] text-[11px] uppercase tracking-[0.14em] font-semibold" data-testid="expense-cancel">
                Cancel
              </button>
              <button onClick={saveExpense} disabled={busy} className="flex-1 h-11 rounded-xl bg-[#0F0E0E] text-[#F5EFE6] text-[11px] uppercase tracking-[0.14em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="expense-save">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <ReceiptText size={14} />} Save to vault
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminAccounts;
